import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Container, Typography, List, ListItem, ListItemText, IconButton, TextField, Button, Box, Modal } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TeamDetailsForm from '../components/TeamDetailsForm'; // TeamDetailsForm modal bileşenini import edin
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from "react-i18next";

const ProjectTeams = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [teams, setTeams] = useState([]);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [teamMessage, setTeamMessage] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamDetailsModalOpen, setTeamDetailsModalOpen] = useState(false); // TeamDetailsForm modal state'i
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Delete modal state
  const [isAuthorized, setIsAuthorized] = useState(false);
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  useEffect(() => {
    fetchTeams();
    fetchUserRole();
  }, [projectId]);

  // Takımları getirme fonksiyonu
  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/teams/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      console.error('Takımlar alınamadı:', error);
    }
  };

  // Takımı silme fonksiyonu
  const handleDeleteTeam = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/teams/${selectedTeamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTeams(); // Takımları yeniden yükle
      setSelectedTeamId(null); // Seçili takımı temizle
      setTeamMessage(t("team_deleted_successfully"));
    } catch (error) {
      console.error('Takım silme hatası:', error);
      setTeamMessage(t("failed_to_delete_team") + (error.response?.data?.error || error.message));
    }
  };

  // Yeni takım oluşturma fonksiyonu
  const handleCreateTeam = async (event) => {
    event.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await api.post(`/api/projects/${projectId}/teams`, { name: teamName, description: teamDescription }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMessage(t("team_created_success"));
      setTeamName('');
      setTeamDescription('');
      setTeamModalOpen(false);
      fetchTeams(); // Yeni takımı listeye eklemek için takımları yeniden yükle
    } catch (error) {
      setTeamMessage(t("team_creation_failed") + (error.response?.data?.error || error.message));
    }
  };

  // TeamDetailsForm modalını açma fonksiyonu
  const viewTeamDetails = (teamId) => {
    setSelectedTeamId(teamId);
    setTeamDetailsModalOpen(true);
  };

  // Silme onay modalını açma fonksiyonu
  const openDeleteModal = (teamId) => {
    setSelectedTeamId(teamId);
    setShowDeleteModal(true); // Silme modalını aç
  };

  const fetchUserRole = async () => {
    try {
        const token = localStorage.getItem('token'); // Make sure the token is set correctly
        if (!token) {
           
            return;
        }

        // API isteğini yap
        const roleResponse = await api.get(`/api/projects/${projectId}/role`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        console.log("rol " + roleResponse.data.roleId);
        if (roleResponse.data.roleId === 1)
        {
            setIsAuthorized(true);
        }
        else
        {
            setIsAuthorized(false);
        }
    } catch (error) {
        // Hata oluşursa yanıtı veya hata mesajını yazdır
        console.error('Error fetching role:', error.response?.data || error.message);
    }
};

  return (
    <Container maxWidth="md">
      <Box className='box-half'>
  <Typography variant="h6" className='header'>
    <span className='header-text'>{t('projectTeams.title')}</span>
    {isAuthorized && (
      <Button variant="contained" className='action-button' style={{ float: "right" }} color="primary" onClick={() => setTeamModalOpen(true)}>
        {t('projectTeams.createTeam')}
      </Button>
    )}
  </Typography>

  {/* Takım listesi */}
  <List className='list'>
    {teams.map((team) => (
      <ListItem className='list-item' key={team.id}>
        <ListItemText
          primary={<span className="list-item-text">{team.name}</span>}
          secondary={<span className="list-item-text">{team.description}</span>}
        />

        {/* TeamDetailsForm'u açmak için buton */}
        <IconButton onClick={() => viewTeamDetails(team.id)}>
          <EditIcon />
        </IconButton>

        {/* Takımı silme butonu */}
        {isAuthorized && (
          <IconButton onClick={() => openDeleteModal(team.id)}>
            <DeleteIcon />
          </IconButton>
        )}
      </ListItem>
    ))}
  </List>
</Box>

{/* Create Team Modal */}
<Modal open={teamModalOpen} onClose={() => setTeamModalOpen(false)}>
  <Box style={{ backgroundColor: 'white', padding: '20px', margin: '50px auto', width: '400px' }}>
    <Typography variant="h5" gutterBottom>
      {t('projectTeams.createTeam')}
    </Typography>
    <form onSubmit={handleCreateTeam}>
      <TextField
        label={t('projectTeams.teamName')}
        fullWidth
        margin="normal"
        value={teamName}
        onChange={(e) => setTeamName(e.target.value)}
      />
      <TextField
        label={t('projectTeams.teamDescription')}
        fullWidth
        margin="normal"
        value={teamDescription}
        onChange={(e) => setTeamDescription(e.target.value)}
      />
      <Button variant="contained" color="primary" type="submit" fullWidth>
        {t('projectTeams.create')}
      </Button>
      {teamMessage && <Typography color="error">{teamMessage}</Typography>}
    </form>
  </Box>
</Modal>

{/* Delete Team Modal */}
<Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
  <Box style={{ backgroundColor: 'white', padding: '20px', margin: '50px auto', width: '400px' }}>
    <Typography variant="h5" gutterBottom>
      {t('projectTeams.confirmDeletion')}
    </Typography>
    <Typography variant="body1" gutterBottom>
      {t('projectTeams.deleteConfirmation')}
    </Typography>
    <Button onClick={handleDeleteTeam} variant="contained" color="secondary" fullWidth>
      {t('projectTeams.delete')}
    </Button>
    <Button onClick={() => setShowDeleteModal(false)} variant="contained" color="primary" fullWidth>
      {t('projectTeams.cancel')}
    </Button>
  </Box>
</Modal>


      {/* TeamDetailsForm Modal */}
      {teamDetailsModalOpen && (
        <TeamDetailsForm
          open={teamDetailsModalOpen}
          onClose={() => setTeamDetailsModalOpen(false)}
          teamId={selectedTeamId}
          projectId={projectId}
          onTeamUpdate={fetchTeams}
        />
      )}
    </Container>
  );
};

export default ProjectTeams;
